import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaNetworkWired,
  FaHistory,
  FaExclamationTriangle,
  FaTable,
  FaChartLine,
} from 'react-icons/fa';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TextField, Button, ButtonGroup, CircularProgress } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';

import { fetchKavachEventLogsHistory, KavachHistoryResponse, KavachTelemetryHistoryTransformedResponse } from '../utils/kavachApiService';
import { EventLogItem, EventLogData } from '../utils/kavachApiTypes';
import KavachHistoryGraph from './KavachHistoryGraph';
import '../kavach-dashboard.scss';

type ViewMode = 'table' | 'graph';

interface PortHistoryRecord {
  id: string;
  timestamp: string;
  deviceId: number;
  deviceName: string;
  deviceIp: string;
  port1Status: boolean;
  port2Status: boolean;
  port3Status: boolean;
  port4Status: boolean;
  portsUp: number;
  portsDown: number;
  responseTime: number;
}

// Date range presets
const getDateRangePresets = () => [
  { label: 'Last 24 Hours', days: 1 },
  { label: 'Last 7 Days', days: 7 },
  { label: 'Last 30 Days', days: 30 },
  { label: 'Last 90 Days', days: 90 },
];

// Calculate date range from preset
const calculateDateRange = (days: number): { startTs: number; endTs: number } => {
  const endTs = Date.now();
  const startTs = endTs - days * 24 * 60 * 60 * 1000;
  return { startTs, endTs };
};

// Parse event log data
const parseEventLogData = (dataString: string): EventLogData | null => {
  try {
    return JSON.parse(dataString);
  } catch {
    return null;
  }
};

// Transform event logs to history records
const transformToHistoryRecords = (eventLogs: any[]): PortHistoryRecord[] => {
  console.log(eventLogs, "eventLogs");
  return eventLogs
    .map((log, index) => {
      const parsed = log;
      if (!parsed) return null;

      const port1Up = parsed.results?.PORT1_STATUS?.value === '1';
      const port2Up = parsed.results?.PORT2_STATUS?.value === '1';
      const port3Up = parsed.results?.PORT3_STATUS?.value === '1';
      const port4Up = parsed.results?.PORT4_STATUS?.value === '1';

      const portsUp = [port1Up, port2Up, port3Up, port4Up].filter(Boolean).length;
      const portsDown = 4 - portsUp;

      return {
        id: `history-${log.dateTime}-${index}`,
        timestamp: log.dateTime,
        deviceId: log.deviceId,
        deviceName: parsed.deviceName || `Device ${log.deviceId}`,
        deviceIp: parsed.deviceIp || '-',
        port1Status: port1Up,
        port2Status: port2Up,
        port3Status: port3Up,
        port4Status: port4Up,
        portsUp,
        portsDown,
        responseTime: parsed.totalResponseTimeMs || 0,
      };
    })
    .filter((record): record is PortHistoryRecord => record !== null);
};

const KavachSwitchHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // State
  const [startTs, setStartTs] = useState<Dayjs | null>(dayjs().subtract(7, 'day'));
  const [endTs, setEndTs] = useState<Dayjs | null>(dayjs());
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [graphPageSize] = useState(500); // Larger page size for graph view

  // API data state
  const [historyData, setHistoryData] = useState<KavachTelemetryHistoryTransformedResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  // Date range presets
  const datePresets = getDateRangePresets();

  // Load history data function using conventional axios method
  const loadHistoryData = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);

    try {
      if (!id) {
        return;
      }
      const queryParams = {
        page: viewMode === 'graph' ? 1 : page + 1,
        size: viewMode === 'graph' ? graphPageSize : pageSize,
        deviceId: id,
        fromDate: startTs ? startTs.valueOf() : undefined,
        toDate: endTs ? endTs.valueOf() : undefined,
      };

      const data = await fetchKavachEventLogsHistory(queryParams);
      setHistoryData(data);
    } catch (error) {
      console.error('Error loading history data:', error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [id, page, pageSize, graphPageSize, startTs, endTs, viewMode]);

  // Initial data load and refetch on param changes
  useEffect(() => {
    loadHistoryData();
  }, [loadHistoryData]);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    loadHistoryData();
  }, [loadHistoryData]);

  // Transform data for display
  const historyRecords = useMemo(() => {
    if (!historyData?.content) return [];
    return transformToHistoryRecords(historyData.content);
  }, [historyData]);

  // Get device name from first record
  const deviceName = useMemo(() => {
    if (historyRecords.length > 0) {
      return historyRecords[0].deviceName;
    }
    return id ? `Device ${id}` : 'All Devices';
  }, [historyRecords, id]);

  // Quick date range selection
  const handleQuickRange = (days: number) => {
    const range = calculateDateRange(days);
    setStartTs(dayjs(range.startTs));
    setEndTs(dayjs(range.endTs));
    setPage(0);
  };

  // Handle search button click
  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  // Format timestamp for display
  const formatTimestamp = (isoString: string): string => {
    try {
      return dayjs(isoString).format('DD/MM/YYYY HH:mm:ss');
    } catch {
      return isoString;
    }
  };

  // Calculate pagination info
  const totalPages = Math.ceil((historyData?.totalElements || 0) / pageSize);

  // Get status class
  const getStatusClass = (portsUp: number) => {
    if (portsUp === 4) return 'healthy';
    if (portsUp >= 2) return 'partial';
    return 'critical';
  };

  const getStatusLabel = (portsUp: number) => {
    if (portsUp === 4) return 'All Up';
    if (portsUp >= 2) return 'Partial';
    return 'Critical';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <div className="kavach-dashboard">
        <div className="kavach-history-page">
          {/* Header */}
          <div className="history-header">
            <div className="header-left">
              <button className="back-btn" onClick={() => navigate('/kavach-dashboard')}>
                <FaArrowLeft /> Back
              </button>
              <div className="title-section">
                <FaNetworkWired className="title-icon" />
                <h1>Switch History</h1>
                {deviceName && <span className="device-name">{deviceName}</span>}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="history-filters">
            <div className="date-pickers">
              <DateTimePicker
                label="From"
                value={startTs}
                onChange={(date) => setStartTs(date)}
                renderInput={(params) => <TextField size="small" {...params} />}
              />
              <DateTimePicker
                label="To"
                value={endTs}
                onChange={(date) => setEndTs(date)}
                renderInput={(params) => <TextField size="small" {...params} />}
              />
              <Button variant="contained" onClick={handleSearch} disabled={isLoading}>
                Search
              </Button>
            </div>

            <div className="quick-ranges">
              <span className="label">Quick Range:</span>
              <ButtonGroup size="small" variant="outlined">
                {datePresets.map((preset) => (
                  <Button key={preset.days} onClick={() => handleQuickRange(preset.days)}>
                    {preset.label}
                  </Button>
                ))}
              </ButtonGroup>
            </div>

            <div className="view-toggle">
              <ButtonGroup size="small">
                <Button
                  variant={viewMode === 'table' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('table')}
                >
                  <FaTable /> Table
                </Button>
                <Button
                  variant={viewMode === 'graph' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('graph')}
                >
                  <FaChartLine /> Graph
                </Button>
              </ButtonGroup>
            </div>
          </div>

          {/* Content */}
          <div className="history-content">
            {isLoading ? (
              <div className="loading-container">
                <CircularProgress />
                <p>Loading history...</p>
              </div>
            ) : isError ? (
              <div className="error-container">
                <FaExclamationTriangle className="error-icon" />
                <p>Failed to load history data. Please try again.</p>
                <Button variant="contained" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            ) : viewMode === 'table' ? (
              <div className="history-table-container">
                {historyRecords.length === 0 ? (
                  <div className="no-data">
                    <FaHistory className="no-data-icon" />
                    <h3>No history data found</h3>
                    <p>No records found for the selected date range.</p>
                  </div>
                ) : (
                  <>
                    <div className="table-scroll">
                      <table className="history-table">
                        <thead>
                          <tr>
                            <th>Timestamp</th>
                            <th>Device</th>
                            <th>IP Address</th>
                            <th>Port 1</th>
                            <th>Port 2</th>
                            <th>Port 3</th>
                            <th>Port 4</th>
                            <th>Status</th>
                            <th>Response (ms)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyRecords.map((record) => (
                            <tr key={record.id}>
                              <td>{formatTimestamp(record.timestamp)}</td>
                              <td>{record.deviceName}</td>
                              <td>{record.deviceIp}</td>
                              <td>
                                <span className={`port-status ${record.port1Status ? 'up' : 'down'}`}>
                                  {record.port1Status ? 'UP' : 'DOWN'}
                                </span>
                              </td>
                              <td>
                                <span className={`port-status ${record.port2Status ? 'up' : 'down'}`}>
                                  {record.port2Status ? 'UP' : 'DOWN'}
                                </span>
                              </td>
                              <td>
                                <span className={`port-status ${record.port3Status ? 'up' : 'down'}`}>
                                  {record.port3Status ? 'UP' : 'DOWN'}
                                </span>
                              </td>
                              <td>
                                <span className={`port-status ${record.port4Status ? 'up' : 'down'}`}>
                                  {record.port4Status ? 'UP' : 'DOWN'}
                                </span>
                              </td>
                              <td>
                                <span className={`health-badge ${getStatusClass(record.portsUp)}`}>
                                  {getStatusLabel(record.portsUp)} ({record.portsUp}/4)
                                </span>
                              </td>
                              <td>{record.responseTime.toFixed(0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="pagination">
                      <span className="pagination-info">
                        Showing {page * pageSize + 1} -{' '}
                        {Math.min((page + 1) * pageSize, historyData?.totalElements || 0)} of{' '}
                        {historyData?.totalElements || 0}
                      </span>
                      <div className="pagination-controls">
                        <Button size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>
                          Previous
                        </Button>
                        <span className="page-number">
                          Page {page + 1} of {totalPages}
                        </span>
                        <Button
                          size="small"
                          disabled={page >= totalPages - 1}
                          onClick={() => setPage(page + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="history-graph-container">
                {historyRecords.length === 0 ? (
                  <div className="no-data">
                    <FaHistory className="no-data-icon" />
                    <h3>No history data found</h3>
                    <p>No records found for the selected date range.</p>
                  </div>
                ) : (
                  <KavachHistoryGraph data={historyRecords} deviceName={deviceName} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default KavachSwitchHistory;
